import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, AlertCircle, RefreshCw, DollarSign, FileText, Download } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const BankReconciliationDashboard = ({ comparisonResult, repassesData, extratoData, onReset }) => {
  if (!comparisonResult) {
    return (
      <Card className="glass-effect border-white/20">
        <CardContent className="p-8 text-center">
          <p className="text-white/70">Nenhum resultado disponível</p>
        </CardContent>
      </Card>
    );
  }

  const { encontrados, naoEncontrados, totais, bate100 } = comparisonResult;

  const formatMoney = (val) => {
    if (typeof val !== 'number') return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const dataGeracao = new Date().toLocaleDateString('pt-BR');
    const horaGeracao = new Date().toLocaleTimeString('pt-BR');
    
    // Título principal
    doc.setFontSize(18);
    doc.setTextColor(40, 40, 40);
    doc.text("Relatório de Conciliação Bancária", 14, 20);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Gerado em: ${dataGeracao} às ${horaGeracao}`, 14, 28);

    // Resumo
    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.text("Resumo da Conciliação", 14, 40);

    doc.autoTable({
      startY: 45,
      head: [['Total Repasses', 'Total Extrato', 'Encontrados', 'Não Encontrados']],
      body: [[
        formatMoney(totais.repasses),
        formatMoney(totais.extrato),
        formatMoney(totais.encontrados) + ` (${totais.percentualEncontrado.toFixed(1)}%)`,
        formatMoney(totais.naoEncontrados)
      ]],
      theme: 'grid',
      headStyles: { fillColor: [41, 128, 185] },
      margin: { left: 14 }
    });

    let lastY = doc.lastAutoTable.finalY + 15;

    // Status
    doc.setFontSize(12);
    if (bate100) {
      doc.setTextColor(46, 204, 113); // Green
      doc.text("Status: 100% Conciliação Bem-Sucedida!", 14, lastY);
    } else {
      doc.setTextColor(231, 76, 60); // Red
      doc.text(`Status: Conciliação Parcial (${naoEncontrados.length} repasses não encontrados)`, 14, lastY);
    }
    
    lastY += 15;

    // Tabela Encontrados
    if (encontrados.length > 0) {
      doc.setFontSize(14);
      doc.setTextColor(40, 40, 40);
      doc.text("Repasses Encontrados e Conciliados", 14, lastY);
      
      const encontradosBody = encontrados.map(item => [
        new Date(item.repasse.data + 'T12:00:00Z').toLocaleDateString('pt-BR'),
        formatMoney(item.repasse.valor),
        formatMoney(item.extrato.valor),
        item.extrato.descricao || item.extrato.historico || item.extrato.tipo || item.repasse.tipo || 'Sem descrição'
      ]);

      doc.autoTable({
        startY: lastY + 5,
        head: [['Data', 'Repasse', 'Banco', 'Descrição']],
        body: encontradosBody,
        theme: 'striped',
        headStyles: { fillColor: [46, 204, 113] }, // Greenish header
        styles: { fontSize: 9 },
      });
      lastY = doc.lastAutoTable.finalY + 15;
    }

    // Tabela Não Encontrados
    if (naoEncontrados.length > 0) {
      // Add a new page if we are too low
      if (lastY > 250) {
        doc.addPage();
        lastY = 20;
      }

      doc.setFontSize(14);
      doc.setTextColor(40, 40, 40);
      doc.text("Não Encontrados no Extrato", 14, lastY);
      
      const naoEncontradosBody = naoEncontrados.map(item => [
        new Date(item.repasse.data + 'T12:00:00Z').toLocaleDateString('pt-BR'),
        formatMoney(item.repasse.valor),
        'Não encontrado'
      ]);

      doc.autoTable({
        startY: lastY + 5,
        head: [['Data', 'Valor do Repasse', 'Status']],
        body: naoEncontradosBody,
        theme: 'striped',
        headStyles: { fillColor: [231, 76, 60] }, // Redish header
        styles: { fontSize: 9 },
      });
    }

    doc.save(`conciliacao_bancaria_${dataGeracao.replace(/\//g, '-')}.pdf`);
  };

  return (
    <div className="space-y-6">
      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Repasses */}
        <Card className="glass-effect border-white/10 border-l-4 border-l-[#0084FF] relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#0084FF] shadow-[0_0_15px_#0084FF]"></div>
          <CardContent className="p-4 flex items-center gap-3 h-full">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#0084FF]/20 text-[#0084FF] flex-shrink-0">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <p className="text-white/70 text-sm font-medium">Total Repasses</p>
              <p className="text-xl font-bold text-white">{formatMoney(totais.repasses)}</p>
              <p className="text-xs text-white/50">{repassesData?.length || 0} {repassesData?.length === 1 ? 'repasse' : 'repasses'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Total Extrato */}
        <Card className="glass-effect border-white/10 border-l-4 border-l-[#7B2CBF] relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#7B2CBF] shadow-[0_0_15px_#7B2CBF]"></div>
          <CardContent className="p-4 flex items-center gap-3 h-full">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#7B2CBF]/20 text-[#7B2CBF] flex-shrink-0">
              <DollarSign className="w-5 h-5" />
            </div>
            <div>
              <p className="text-white/70 text-sm font-medium">Total Extrato</p>
              <p className="text-xl font-bold text-[#7B2CBF]">{formatMoney(totais.extrato)}</p>
              <p className="text-xs text-white/50">{extratoData?.length || 0} {extratoData?.length === 1 ? 'crédito' : 'créditos'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Encontrados */}
        <Card className="glass-effect border-white/10 border-l-4 border-l-[#00D26A] relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#00D26A] shadow-[0_0_15px_#00D26A]"></div>
          <CardContent className="p-4 flex items-center gap-3 h-full">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#00D26A]/20 text-[#00D26A] flex-shrink-0">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <p className="text-white/70 text-sm font-medium">Encontrados</p>
              <p className="text-xl font-bold text-[#00D26A]">{formatMoney(totais.encontrados)}</p>
              <p className="text-xs text-white/50">{encontrados.length} ({totais.percentualEncontrado.toFixed(1)}%)</p>
            </div>
          </CardContent>
        </Card>

        {/* Não Encontrados */}
        <Card className="glass-effect border-white/10 border-l-4 border-l-[#E11D48] relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#E11D48] shadow-[0_0_15px_#E11D48]"></div>
          <CardContent className="p-4 flex items-center gap-3 h-full">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#E11D48]/20 text-[#E11D48] flex-shrink-0">
              <XCircle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-white/70 text-sm font-medium">Não Encontrados</p>
              <p className="text-xl font-bold text-[#E11D48]">{formatMoney(totais.naoEncontrados)}</p>
              <p className="text-xs text-white/50">{naoEncontrados.length} repasses</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Geral */}
      <Card className={`glass-effect ${bate100 ? 'border-green-500/50' : 'border-yellow-500/50'}`}>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            {bate100 ? (
              <>
                <CheckCircle2 className="w-8 h-8 text-green-400" />
                <div>
                  <h3 className="text-xl font-bold text-green-400">100% Conciliação Bem-Sucedida!</h3>
                  <p className="text-white/70 text-sm mt-1">
                    Todos os repasses foram encontrados no extrato bancário.
                  </p>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="w-8 h-8 text-yellow-400" />
                <div>
                  <h3 className="text-xl font-bold text-yellow-400">Conciliação Parcial</h3>
                  <p className="text-white/70 text-sm mt-1">
                    {naoEncontrados.length} {naoEncontrados.length === 1 ? 'repasse não foi encontrado' : 'repasses não foram encontrados'} no extrato.
                  </p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Área de Tabelas Dinâmica */}
      <div className={`grid gap-6 ${encontrados.length > 0 && naoEncontrados.length > 0 ? 'xl:grid-cols-5' : 'grid-cols-1'}`}>
        
        {/* Tabela de Encontrados */}
        {encontrados.length > 0 && (
          <Card className={`glass-effect border-green-500/30 ${naoEncontrados.length > 0 ? 'xl:col-span-3' : ''}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-400">
                <CheckCircle2 className="w-5 h-5" />
                Repasses Encontrados e Conciliados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-white whitespace-nowrap">Data</TableHead>
                      <TableHead className="text-white whitespace-nowrap">Repasse</TableHead>
                      <TableHead className="text-white whitespace-nowrap">Banco</TableHead>
                      <TableHead className="text-white">Descrição</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {encontrados.map((item, idx) => (
                      <TableRow key={idx} className="border-white/10">
                        <TableCell className="text-white/90 whitespace-nowrap">
                          {new Date(item.repasse.data + 'T12:00:00Z').toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell className="text-green-400 font-semibold whitespace-nowrap">
                          {formatMoney(item.repasse.valor)}
                        </TableCell>
                        <TableCell className="text-green-400 font-semibold whitespace-nowrap">
                          {formatMoney(item.extrato.valor)}
                        </TableCell>
                        <TableCell className="text-white/70 text-sm">
                          {item.extrato.descricao || item.extrato.historico || item.extrato.tipo || item.repasse.tipo || 'Sem descrição'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabela de Não Encontrados */}
        {naoEncontrados.length > 0 && (
          <Card className={`glass-effect border-red-500/30 ${encontrados.length > 0 ? 'xl:col-span-2' : ''}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-400 text-base sm:text-lg">
                <XCircle className="w-5 h-5" />
                Não Encontrados no Extrato
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-white whitespace-nowrap">Data</TableHead>
                      <TableHead className="text-white whitespace-nowrap">Valor</TableHead>
                      <TableHead className="text-white whitespace-nowrap">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {naoEncontrados.map((item, idx) => (
                      <TableRow key={idx} className="border-white/10">
                        <TableCell className="text-white/90 whitespace-nowrap">
                          {new Date(item.repasse.data + 'T12:00:00Z').toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell className="text-white/90 font-semibold whitespace-nowrap">
                          {formatMoney(item.repasse.valor)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/20 text-red-400 text-xs">
                            <XCircle className="w-3 h-3" />
                            Não encontrado
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Botões Ação */}
      <div className="flex justify-center gap-4">
        <Button
          onClick={handleExportPDF}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Download className="w-4 h-4 mr-2" />
          Baixar PDF
        </Button>

        <Button
          onClick={onReset}
          variant="outline"
          className="border-white/20 text-white hover:bg-white/10"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Nova Conciliação
        </Button>
      </div>
    </div>
  );
};

export default BankReconciliationDashboard;

